import { FileTypeEnum } from '$/utils/upload';
import { create } from 'zustand';

export type ManualEraserType = 'smear' | 'rect';

/** 单张去水印模式：自动识别与两种手动选区始终互斥。 */
export type SingleWatermarkMode = 'auto' | ManualEraserType;

export type BatchImageStatus = 'normal' | 'processing' | 'success' | 'failed' | 'unrecognized' | 'downloadable';

export interface BatchImageItem {
  id: string;
  status: BatchImageStatus;
  url: string;
  type?: FileTypeEnum;
  /** 视频封面图 URL（视频项可选） */
  coverUrl?: string;
  /** 视频素材展示时长（秒），保留原有展示口径 */
  duration?: number;
  /** 视频素材接口参数时长（秒），用于 cost/submit params.video_duration */
  requestDuration?: number;
  width?: number;
  height?: number;
  resultUrl?: string;
  /** 已拿到 resultUrl 但结果图仍在加载中，用于保持 loading 遮罩直到图片 onLoad */
  resultImageLoading?: boolean;
  progress?: number;
  errorText?: string;
  selected?: boolean;
  selectable?: boolean;
  /** 服务端任务 id（msgid），创建任务后由 ids 映射绑定 */
  taskId?: string;
}

interface AIWatermarkStore {
  singleOriginalImage: string;
  singleResultImage: string;
  /** 单张模式最近一次生成结果对应的服务端任务 id。 */
  singleResultTaskId: string;
  singleCompareIng: boolean;
  /** 单张上传的媒体类型：image 或 video */
  singleMediaType: 'image' | 'video';
  /** 单张视频素材展示时长（秒），保留原有展示口径 */
  singleVideoDuration?: number;
  /** 单张视频素材接口参数时长（秒），用于 cost/submit params.video_duration */
  singleVideoRequestDuration?: number;
  mode: 'single' | 'batch';
  manualEnableEraser: boolean;
  brushSize: number;
  singleEraserLoading: boolean;
  /** 单张当前去水印模式，进入页面时默认开启自动去水印。 */
  singleWatermarkMode: SingleWatermarkMode;
  batchImages: BatchImageItem[];
  batchMaxCount: number;
  batchProcessingMode: boolean;
  previewImageIndex: number | null;
  /** 批量消除进行中 */
  batchEraserLoading: boolean;
  /** 批量是否启用自动去水印，进入页面时默认开启。 */
  batchAutoWatermarkEnabled: boolean;
  /** 单张模式「重新上传」触发打开文件选择，与批量分离避免两个 ImageUploader 同 key 重复弹窗 */
  singleReuploadSignal: number;
  /** 批量模式「重新上传」触发打开文件选择 */
  batchReuploadSignal: number;
  /** 批量模式：点击重新上传后为 true，下次选择图片确认上传后替换列表并置回 false */
  batchReuploadPending: boolean;
}

const useAIWatermarkStore = create<AIWatermarkStore>(() => ({
  singleOriginalImage: '',
  singleResultImage: '',
  singleResultTaskId: '',
  singleCompareIng: false,
  singleMediaType: 'image',
  singleVideoDuration: undefined,
  singleVideoRequestDuration: undefined,
  mode: 'single',
  singleEraserLoading: false,
  manualEnableEraser: false,
  brushSize: 20,
  singleWatermarkMode: 'auto',
  batchImages: [],
  batchMaxCount: 30,
  batchProcessingMode: false,
  previewImageIndex: null,
  batchEraserLoading: false,
  batchAutoWatermarkEnabled: true,
  singleReuploadSignal: 0,
  batchReuploadSignal: 0,
  batchReuploadPending: false,
}));

export default useAIWatermarkStore;

export function setSingleOriginalImage(singleOriginalImage: string) {
  useAIWatermarkStore.setState(() => ({ singleOriginalImage }));
}

/**
 * 设置单张模式结果图，清空结果时同步清空任务 id。
 */
export function setSingleResultImage(singleResultImage: string) {
  useAIWatermarkStore.setState(() => ({
    singleResultImage,
    ...(!singleResultImage ? { singleResultTaskId: '' } : {}),
  }));
}

/**
 * 设置单张模式结果对应的服务端任务 id。
 */
export function setSingleResultTaskId(singleResultTaskId: string) {
  useAIWatermarkStore.setState(() => ({ singleResultTaskId }));
}

export function setSingleMediaType(singleMediaType: 'image' | 'video') {
  useAIWatermarkStore.setState(() => ({ singleMediaType }));
}

/**
 * 记录单张视频素材展示时长，保留原有展示口径。
 */
export function setSingleVideoDuration(singleVideoDuration?: number) {
  useAIWatermarkStore.setState(() => ({ singleVideoDuration }));
}

/**
 * 记录单张视频素材接口参数时长，供 cost/submit 写入 params.video_duration。
 */
export function setSingleVideoRequestDuration(singleVideoRequestDuration?: number) {
  useAIWatermarkStore.setState(() => ({ singleVideoRequestDuration }));
}

/**
 * 设置当前模式
 * 切到单张时退出批量处理
 */
export function setMode(mode: 'single' | 'batch') {
  if (mode === 'single') {
    useAIWatermarkStore.setState((state) => ({
      mode: 'single',
      batchProcessingMode: false,
      previewImageIndex: null,
      batchImages: state.batchImages.map((item) => ({ ...item, selected: false })),
    }));
  } else {
    useAIWatermarkStore.setState(() => ({ mode: 'batch' }));
  }
}

export function setBrushSize(brushSize: number) {
  useAIWatermarkStore.setState(() => ({ brushSize }));
}

export function setSingleEraserLoading(singleEraserLoading: boolean) {
  useAIWatermarkStore.setState(() => ({ singleEraserLoading }));
}

/** 设置单张去水印模式，由联合状态保证自动与手动工具互斥。 */
export function setSingleWatermarkMode(singleWatermarkMode: SingleWatermarkMode) {
  useAIWatermarkStore.setState(() => ({ singleWatermarkMode }));
}

export function setManualEnableEraser(manualEnableEraser: boolean) {
  useAIWatermarkStore.setState(() => ({ manualEnableEraser }));
}

export function setSingleCompareIng(singleCompareIng: boolean) {
  useAIWatermarkStore.setState(() => ({ singleCompareIng }));
}

export function updateBatchImage(
  id: string,
  patch: Partial<Pick<BatchImageItem, 'status' | 'progress' | 'resultUrl' | 'resultImageLoading' | 'errorText' | 'taskId' | 'width' | 'height'>>,
) {
  useAIWatermarkStore.setState((state) => ({
    batchImages: state.batchImages.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  }));
}

export function setBatchEraserLoading(loading: boolean) {
  useAIWatermarkStore.setState(() => ({ batchEraserLoading: loading }));
}

/** 设置批量自动去水印开关。 */
export function setBatchAutoWatermarkEnabled(batchAutoWatermarkEnabled: boolean) {
  useAIWatermarkStore.setState(() => ({ batchAutoWatermarkEnabled }));
}

export function setBatchImages(batchImages: BatchImageItem[]) {
  useAIWatermarkStore.setState(() => ({ batchImages }));
}

export function addBatchImages(newItems: BatchImageItem[]) {
  useAIWatermarkStore.setState((state) => ({
    batchImages: [...newItems, ...state.batchImages],
  }));
}

export function setBatchProcessingMode(active: boolean) {
  useAIWatermarkStore.setState((state) => {
    const batchImages = active
      ? state.batchImages
      : state.batchImages.map((item) => ({ ...item, selected: false }));
    return { batchProcessingMode: active, batchImages };
  });
}

export function toggleBatchImageSelected(id: string) {
  useAIWatermarkStore.setState((state) => {
    const batchImages = state.batchImages.map((item) => {
      if (item.id === id) return { ...item, selected: !item.selected };
      return item;
    });
    return { batchImages };
  });
}

export function removeBatchImage(id: string) {
  useAIWatermarkStore.setState((state) => {
    const { batchImages: stateBatchImages, previewImageIndex } = state;
    const removedIndex = stateBatchImages.findIndex((img) => img.id === id);
    const batchImages = stateBatchImages.filter((img) => img.id !== id);

    let nextPreviewIndex = previewImageIndex;
    if (previewImageIndex !== null && removedIndex >= 0) {
      if (removedIndex === previewImageIndex) {
        nextPreviewIndex = null;
      } else if (removedIndex < previewImageIndex) {
        nextPreviewIndex = Math.max(0, previewImageIndex - 1);
      }
    }

    return { batchImages, previewImageIndex: nextPreviewIndex };
  });
}

export function setPreviewImageIndex(index: number | null) {
  useAIWatermarkStore.setState(() => ({ previewImageIndex: index }));
}

export function cancelBatchSelection() {
  useAIWatermarkStore.setState((state) => ({
    batchProcessingMode: false,
    batchImages: state.batchImages.map((item) => ({ ...item, selected: false })),
  }));
}

export function triggerBatchReupload() {
  useAIWatermarkStore.setState((state) => ({
    batchReuploadSignal: state.batchReuploadSignal + 1,
  }));
}

export function triggerSingleReupload() {
  useAIWatermarkStore.setState((state) => ({
    singleReuploadSignal: state.singleReuploadSignal + 1,
  }));
}

export function setBatchReuploadPending(pending: boolean) {
  useAIWatermarkStore.setState(() => ({ batchReuploadPending: pending }));
}

/**
 * 重置 AI Watermark 页面 store，避免再次进入时沿用旧数据
 *
 * @export
 */
export function resetAIWatermarkStore() {
  useAIWatermarkStore.setState(() => ({
    singleOriginalImage: '',
    singleResultImage: '',
    singleResultTaskId: '',
    singleCompareIng: false,
    singleMediaType: 'image',
    singleVideoDuration: undefined,
    singleVideoRequestDuration: undefined,
    mode: 'single',
    manualEnableEraser: false,
    brushSize: 20,
    singleEraserLoading: false,
    singleWatermarkMode: 'auto',
    batchImages: [],
    batchProcessingMode: false,
    previewImageIndex: null,
    batchEraserLoading: false,
    batchAutoWatermarkEnabled: true,
    singleReuploadSignal: 0,
    batchReuploadSignal: 0,
    batchReuploadPending: false,
  }));
}
